import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, TrashIcon, EyeIcon, ArrowDownTrayIcon, VideoCameraIcon, StopIcon } from '@heroicons/react/24/outline';
import { IVideoLipsync, VideoLipsyncStatus, IUserModel } from '@packages/global';
import { videoLipsyncApi } from '../../services/user/videoLipsync.api';
import StatusBadge from './StatusBadge';
import LazyVideo from '../common/LazyVideo';
import MediaPreviewModal from '../common/MediaPreviewModal';

interface VideoLipsyncCardProps {
  item: IVideoLipsync;
  models: IUserModel[];
  onEdit: (item: IVideoLipsync) => void;
  onDelete: (id: string) => void;
}

export default function VideoLipsyncCard({ item, models, onEdit, onDelete }: VideoLipsyncCardProps) {
  const { t, i18n } = useTranslation(['generation', 'common']);
  const [showPreview, setShowPreview] = useState(false);
  const [showOriginalVideo, setShowOriginalVideo] = useState(false);

  const getModelName = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    return model ? model.title : modelId;
  };

  const formatDate = (date: string | Date) => new Date(date).toLocaleString(i18n.language);

  const handleDownload = async () => {
    if (!item.resultVideoUrl) return;
    try {
      const response = await fetch(item.resultVideoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `video-lipsync-${item.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('下载失败:', error);
    }
  };

  const handleCancel = async () => {
    try { await videoLipsyncApi.cancelVideoLipsync(item.id); } catch (error) { console.error('取消任务失败:', error); }
  };

  const handleCardClick = () => { if (item.resultVideoUrl) setShowPreview(true); };

  return (
    <div
      className={`relative w-60 h-80 overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle hover:shadow-[0_0_20px_rgba(255,92,0,0.15)] hover:border-primary hover:-translate-y-1 transition-all duration-300 ${item.resultVideoUrl ? 'cursor-pointer' : ''}`}
      onClick={handleCardClick}
    >
      <div className="absolute inset-0">
        {item.resultVideoUrl ? (
          <LazyVideo src={item.resultVideoUrl} className="w-full h-full object-cover" placeholderClassName="w-full h-full" autoPlayOnHover />
        ) : (
          <div className="w-full h-full bg-bg-elevated flex items-center justify-center">
            {item.status === VideoLipsyncStatus.PENDING || item.status === VideoLipsyncStatus.SUBMITTED || item.status === VideoLipsyncStatus.PROCESSING ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-400 text-sm">
                  {item.status === VideoLipsyncStatus.PENDING ? t('generation:lipsync.card.pendingStatus') : item.status === VideoLipsyncStatus.SUBMITTED ? t('generation:lipsync.card.submittedStatus') : t('generation:lipsync.card.processingStatus')}
                </p>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <div className="w-16 h-16 bg-bg-form rounded-lg mx-auto mb-2 flex items-center justify-center"><VideoCameraIcon className="w-8 h-8" /></div>
                <p className="text-sm">{t('generation:lipsync.card.noResult')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="absolute top-3 right-3"><StatusBadge status={item.status} /></div>
      <div className="absolute top-3 left-3" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => setShowOriginalVideo(true)} className="bg-black/50 text-white/90 px-2 py-1 rounded-md text-xs font-medium border border-white/20 hover:bg-black/70 transition-colors backdrop-blur-sm cursor-pointer">{t('generation:lipsync.card.originalVideo')}</button>
      </div>

      <div className="absolute inset-x-0 bottom-0 pt-20 pb-4 px-4">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(17,24,39,0.95) 0%, rgba(17,24,39,0.8) 40%, rgba(17,24,39,0.4) 70%, transparent 100%)' }}></div>
        <div className="relative z-10">
          <h4 className="font-semibold text-white truncate mb-2">{getModelName(item.modelId)}</h4>
          <div className="text-sm text-white/80 mb-2">{t('generation:lipsync.card.lipsyncTask')}</div>
          <div className="text-xs text-white/60 mb-3">{formatDate(item.createdAt)}</div>
          {item.status === VideoLipsyncStatus.FAILED && item.resultMessage && (
            <div className="text-xs text-red-400 mb-3 line-clamp-1" title={item.resultMessage}>{item.resultMessage}</div>
          )}
          <div className="flex justify-end items-center text-xs" onClick={(e) => e.stopPropagation()}>
            <div className="flex gap-1">
              {(item.status === VideoLipsyncStatus.PENDING || item.status === VideoLipsyncStatus.PROCESSING) && (
                <button onClick={handleCancel} className="p-1.5 text-white bg-orange-600/80 hover:bg-orange-600 rounded transition-colors" title={t('generation:lipsync.card.cancelTask')}><StopIcon className="h-3 w-3" /></button>
              )}
              {item.resultVideoUrl && (
                <>
                  <button onClick={() => setShowPreview(true)} className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-all backdrop-blur-sm" title={t('generation:lipsync.card.previewResult')}><EyeIcon className="h-4 w-4" /></button>
                  <button onClick={handleDownload} className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-all backdrop-blur-sm" title={t('generation:lipsync.card.downloadResult')}><ArrowDownTrayIcon className="h-4 w-4" /></button>
                </>
              )}
              <button onClick={() => onEdit(item)} className="p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-all backdrop-blur-sm" title={t('generation:lipsync.card.createFromThis')}><PencilIcon className="h-4 w-4" /></button>
              <button onClick={() => onDelete(item.id)} className="p-1.5 rounded-lg bg-black/40 hover:bg-red-500/80 text-white/80 hover:text-white transition-all backdrop-blur-sm"><TrashIcon className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </div>

      <MediaPreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} type="video" src={item.resultVideoUrl || ''} />
      <MediaPreviewModal isOpen={showOriginalVideo} onClose={() => setShowOriginalVideo(false)} type="video" src={item.videoUrl || ''} alt={t('generation:lipsync.card.originalVideo')} />
    </div>
  );
}
